import { DOCUMENT } from '@angular/common';
import { Injectable, RendererFactory2, computed, inject, signal } from '@angular/core';
import { MediaLightboxItemContract, MediaLightboxStateContract } from '@shared/components/media/runtime/media-runtime.contract';
import { createMediaLightboxScrollLock } from './media-lightbox-scroll-lock.util';

const EMPTY_LIGHTBOX_STATE: MediaLightboxStateContract = {
  visible: false,
  items: [],
  activeIndex: 0,
};

@Injectable({ providedIn: 'root' })
export class MediaLightboxService {
  private readonly documentRef = inject(DOCUMENT, { optional: true });
  private readonly renderer = inject(RendererFactory2).createRenderer(null, null);
  private readonly scrollLock = createMediaLightboxScrollLock(this.documentRef, this.renderer);

  private readonly lightboxState = signal<MediaLightboxStateContract>(EMPTY_LIGHTBOX_STATE);

  readonly state = this.lightboxState.asReadonly();
  readonly media = computed<MediaLightboxItemContract[]>(() => this.lightboxState().items);
  readonly isVisible = computed<boolean>(() => this.lightboxState().visible);
  readonly index = computed<number>(() => this.lightboxState().activeIndex);
  readonly shouldRender = computed<boolean>(() => this.lightboxState().items.length > 0);

  open(media: MediaLightboxItemContract[], startIndex = 0): void {
    if (!media || media.length === 0) {
      return;
    }

    const validIndex = this.clampIndex(startIndex, media.length);

    this.lightboxState.set({
      visible: true,
      items: [...media],
      activeIndex: validIndex,
    });

    this.scrollLock.lock();
  }

  close(): void {
    if (!this.lightboxState().visible) {
      return;
    }

    this.lightboxState.update((state) => ({
      ...state,
      visible: false,
    }));

    this.scrollLock.unlock();
  }

  finalizeClose(): void {
    const state = this.lightboxState();
    if (state.visible) {
      return;
    }

    if (state.items.length === 0 && state.activeIndex === 0) {
      this.scrollLock.reset();
      return;
    }

    this.lightboxState.set(EMPTY_LIGHTBOX_STATE);
    this.scrollLock.reset();
  }

  next(): void {
    const state = this.lightboxState();
    const mediaLength = state.items.length;
    if (mediaLength <= 1) {
      return;
    }

    const nextIndex = (state.activeIndex + 1) % mediaLength;
    this.setIndex(nextIndex);
  }

  previous(): void {
    const state = this.lightboxState();
    const mediaLength = state.items.length;
    if (mediaLength <= 1) {
      return;
    }

    const prevIndex = state.activeIndex === 0 ? mediaLength - 1 : state.activeIndex - 1;
    this.setIndex(prevIndex);
  }

  setIndex(index: number): void {
    const state = this.lightboxState();
    if (state.items.length === 0) {
      return;
    }

    const nextIndex = this.clampIndex(index, state.items.length);
    if (nextIndex === state.activeIndex) {
      return;
    }

    this.lightboxState.update((currentState) => ({
      ...currentState,
      activeIndex: nextIndex,
    }));
  }

  getCurrentMedia(): MediaLightboxItemContract | null {
    const state = this.lightboxState();
    return state.items[state.activeIndex] ?? null;
  }

  isFirst(): boolean {
    return this.lightboxState().activeIndex === 0;
  }

  isLast(): boolean {
    const state = this.lightboxState();
    return state.items.length > 0 && state.activeIndex === state.items.length - 1;
  }

  private clampIndex(index: number, itemCount: number): number {
    if (itemCount <= 0) {
      return 0;
    }
    return Math.max(0, Math.min(index, itemCount - 1));
  }
}
